import { ReactElement, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, RouterProvider } from 'react-router-dom'
import { useFeatureFlagEnabled } from 'posthog-js/react'

import InternalAdminRoute from './components/InternalAdminRoute'
import Toast from './components/Toast'
import { useToast } from './components/Toast/ToastContext'
import WisprInternalRoute from './components/WisprInternalRoute'
import { useFlowStateGenerationEnabled } from './hooks/useFlowStateGenerationEnabled'
import { usePricing } from './hooks/usePricing'
import AnalyticsPortalPage from './pages/app/AnalyticsPortal'
import AnnotationLeaderboardPage from './pages/app/AnnotationLeaderboard'
import AppLayout from './pages/app/AppLayout'
import AttributionObservabilityPage from './pages/app/AttributionObservability'
import AttributionViewerPage from './pages/app/AttributionViewer'
import ModelComparisonPage from './pages/app/AttributionViewer/compare/ModelComparisonPage'
import TurnPreferenceResultsPage from './pages/app/AttributionViewer/turnPreference/TurnPreferenceResultsPage'
import AuditLogsPage, { AUDIT_LOGS_TAB_SLUGS } from './pages/app/AuditLogs'
import CampaignDetailPage from './pages/app/CampaignDetail'
import CampaignsPage from './pages/app/Campaigns'
import DictionarySnippetsRoute, {
  DICTIONARY_SNIPPETS_BASE_PATH,
  DICTIONARY_SNIPPETS_TAB_SLUGS,
} from './pages/app/DictionarySnippets'
import DomainManagementPage from './pages/app/DomainManagement'
import EnterprisesPage from './pages/app/Enterprises'
import FlowStatePage from './pages/app/FlowState'
import ForgotPasswordPage from './pages/app/ForgotPassword'
import GetStartedPage from './pages/app/GetStarted'
import HomePage from './pages/app/Home'
import LoginPage from './pages/app/Login'
import NotetakerLaunchPage from './pages/app/NotetakerLaunch'
import NotFoundPage from './pages/app/NotFound'
import NotificationsPage from './pages/app/Notifications'
import PrereadFeedbackPage, { PrereadFeedbackIndexPage } from './pages/app/PrereadFeedback'
import PrivacyPage from './pages/app/Privacy'
import ReportedMeetingsPage from './pages/app/ReportedMeetings'
import SettingsPage, { SETTINGS_TAB_SLUGS } from './pages/app/Settings'
import SummaryFeedbackPage, { SummaryFeedbackIndexPage } from './pages/app/SummaryFeedback'
import TeamPage from './pages/app/Team'
import UsagePage from './pages/app/Usage'
import UsageV2Page from './pages/app/UsageV2'
import UsageV2MemberDetailPage from './pages/app/UsageV2/MemberDetail'
import UsersPage from './pages/app/Users'
import PaymentPage from './pages/auth/Payment'
import { fetchTeamDomainStatusThunk } from './store/features/domain/domainSlice'
import { getEnterpriseThunk, getSubscriptionThunk, selectIsAdmin } from './store/features/user/userSlice'
import { getAuthSession } from './utils/auth'
import { hasPendingAuthCallbackTokens } from './utils/authCallback'
import AuthCheck from './utils/AuthCheck'
import { AppDispatch } from './store'

import { sentryCreateBrowserRouter } from '@/utils/sentry'

const UsageRoute = () => {
  const isAdmin = useSelector(selectIsAdmin)
  const v2Enabled = useFeatureFlagEnabled('team-insights')
  if (!isAdmin) return <Navigate to="/app/home" replace />
  return v2Enabled ? <UsageV2Page /> : <UsagePage />
}

// The detail page is v2-only; v1 has no member-detail surface. Wait for the
// flag to resolve before deciding — otherwise a deep link briefly redirects
// out before posthog answers.
const UsageMemberDetailRoute = () => {
  const v2Enabled = useFeatureFlagEnabled('team-insights')
  if (v2Enabled === undefined) return null
  return v2Enabled ? <UsageV2MemberDetailPage /> : <Navigate to="/app/usage" replace />
}

const FlowStateRoute = () => {
  const isAdmin = useSelector(selectIsAdmin)
  const enabled = useFlowStateGenerationEnabled()
  if (!isAdmin) return <Navigate to="/app/home" replace />
  if (enabled === undefined) return null
  return enabled ? <FlowStatePage /> : <Navigate to="/app/home" replace />
}

const AuditLogsRoute = () => {
  const isAdmin = useSelector(selectIsAdmin)
  // The SIEM tab was reachable by any enterprise admin before this PR added the Events tab and
  // its `audit-logs-ui` rollout flag — gating the whole page here would newly lock out existing
  // SIEM-configuration users. AuditLogsPage gates the Events tab alone on that flag.
  if (!isAdmin) return <Navigate to="/app/home" replace />
  return <AuditLogsPage />
}

// Any Wispr account may open or annotate a meeting; the dashboard hides its admin-only metrics itself.
const AttributionViewerRoute = ({ overview = false }: { overview?: boolean }) => (
  <WisprInternalRoute>{overview ? <AttributionObservabilityPage /> : <AttributionViewerPage />}</WisprInternalRoute>
)

// Temporary launch surface, no sidebar entry — direct link only. Routed outside AppLayout so
// the framed page owns the whole viewport, which also means the impersonation redirect
// AppLayout applies to internal routes has to live here. Delete with the page after 2026-08-20.
const NotetakerLaunchRoute = () => (
  <InternalAdminRoute>
    <NotetakerLaunchPage />
  </InternalAdminRoute>
)

const appRoutes = [
  { path: '', element: <Navigate to="/app/home" replace /> },
  {
    path: 'settings',
    children: [
      {
        path: '',
        element: <Navigate to="/app/settings/organization" replace />,
      },
      ...SETTINGS_TAB_SLUGS.map((slug) => ({
        path: slug,
        element: <SettingsPage />,
      })),
      { path: 'other', element: <SettingsPage /> },
    ],
  },
  { path: 'usage', element: <UsageRoute /> },
  { path: 'usage/members/:userId', element: <UsageMemberDetailRoute /> },
  {
    path: 'dictionary',
    children: [
      {
        path: '',
        element: <Navigate to={`${DICTIONARY_SNIPPETS_BASE_PATH}/${DICTIONARY_SNIPPETS_TAB_SLUGS[0]}`} replace />,
      },
      ...DICTIONARY_SNIPPETS_TAB_SLUGS.map((slug) => ({
        path: slug,
        element: <DictionarySnippetsRoute />,
      })),
    ],
  },
  { path: 'analytics-portal', element: <AnalyticsPortalPage /> },
  { path: 'notifications', element: <NotificationsPage /> },
  {
    path: 'domain-management',
    children: [
      {
        path: '',
        element: <Navigate to="/app/domain-management/blocked" replace />,
      },
      { path: 'blocked', element: <DomainManagementPage /> },
      { path: 'public', element: <DomainManagementPage /> },
      { path: 'university', element: <DomainManagementPage /> },
    ],
  },
  {
    path: 'blocked-domains',
    element: <Navigate to="/app/domain-management/blocked" replace />,
  },
  {
    path: 'billing-ops',
    element: <Navigate to="/app/enterprises/billing/ops" replace />,
  },
  { path: 'flow-state', element: <FlowStateRoute /> },
  { path: 'attribution', element: <AttributionViewerRoute overview /> },
  { path: 'attribution/viewer', element: <Navigate to="/app/attribution" replace /> },
  {
    path: 'attribution/turn-preference/results',
    element: (
      <WisprInternalRoute>
        <TurnPreferenceResultsPage />
      </WisprInternalRoute>
    ),
  },
  { path: 'attribution/:meetingId', element: <AttributionViewerRoute /> },
  {
    path: 'attribution/:meetingId/compare',
    element: (
      <WisprInternalRoute>
        <ModelComparisonPage />
      </WisprInternalRoute>
    ),
  },
  {
    path: 'annotation',
    element: (
      <WisprInternalRoute>
        <AnnotationLeaderboardPage />
      </WisprInternalRoute>
    ),
  },
  {
    path: 'reported-meetings',
    element: (
      <InternalAdminRoute>
        <ReportedMeetingsPage />
      </InternalAdminRoute>
    ),
  },
  {
    path: 'summary-feedback',
    element: (
      <InternalAdminRoute>
        <SummaryFeedbackIndexPage />
      </InternalAdminRoute>
    ),
  },
  {
    path: 'preread-feedback',
    element: (
      <InternalAdminRoute>
        <PrereadFeedbackIndexPage />
      </InternalAdminRoute>
    ),
  },
  {
    path: 'preread-feedback/:id',
    element: (
      <InternalAdminRoute>
        <PrereadFeedbackPage />
      </InternalAdminRoute>
    ),
  },
  {
    path: 'summary-feedback/:id',
    element: (
      <InternalAdminRoute>
        <SummaryFeedbackPage />
      </InternalAdminRoute>
    ),
  },
  { path: 'campaigns', element: <CampaignsPage /> },
  { path: 'campaigns/:campaignId', element: <CampaignDetailPage /> },
  { path: 'team', element: <TeamPage /> },
  {
    path: 'audit-logs',
    children: [
      {
        path: '',
        element: <Navigate to="/app/audit-logs/events" replace />,
      },
      ...AUDIT_LOGS_TAB_SLUGS.map((slug) => ({ path: slug, element: <AuditLogsRoute /> })),
    ],
  },
  {
    path: 'enterprises',
    children: [
      {
        path: '',
        element: <Navigate to="/app/enterprises/create" replace />,
      },
      { path: 'create', element: <EnterprisesPage /> },
      { path: 'create-sales-owned', element: <EnterprisesPage /> },
      { path: 'create-experimental', element: <Navigate to="/app/enterprises/create-sales-owned" replace /> },
      { path: 'manage', element: <EnterprisesPage /> },
      { path: 'pilots', element: <EnterprisesPage /> },
      {
        path: 'billing',
        children: [
          {
            path: '',
            element: <Navigate to="/app/enterprises/billing/custom-pricing" replace />,
          },
          { path: 'custom-pricing', element: <EnterprisesPage /> },
          { path: 'ops', element: <EnterprisesPage /> },
        ],
      },
      { path: 'other', element: <EnterprisesPage /> },
    ],
  },
  { path: 'users', element: <UsersPage /> },
  { path: 'home', element: <HomePage /> },
  { path: 'privacy', element: <PrivacyPage /> },
  { path: '*', element: <NotFoundPage /> },
]

// wrap route navigation with Sentry error boundary
const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: <AuthCheck />,
    children: [
      { path: 'login', element: <LoginPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'get-started', element: <GetStartedPage /> },
      { path: 'payment', element: <PaymentPage /> },
      { path: 'app/notetaker-launch', element: <NotetakerLaunchRoute /> },
      { path: 'app', element: <AppLayout />, children: appRoutes },
    ],
  },
])

const App = (): ReactElement => {
  const dispatch = useDispatch<AppDispatch>()
  const { toastProps } = useToast()

  // Initialize pricing store once at app root to avoid multiple initialization calls
  usePricing()

  useEffect(() => {
    const fetchEnterprise = async () => {
      if (hasPendingAuthCallbackTokens()) return
      const session = await getAuthSession()
      const accessToken = session?.accessToken || undefined
      if (!accessToken) {
        return
      }
      const awaitingSubscription = !!localStorage.getItem('awaiting-stripe-subscription')
      localStorage.removeItem('awaiting-stripe-subscription')

      dispatch(fetchTeamDomainStatusThunk())
      dispatch(getEnterpriseThunk({ needSubscription: awaitingSubscription }))
      dispatch(getSubscriptionThunk())
    }

    fetchEnterprise()
  }, [dispatch])

  return (
    <>
      <RouterProvider router={router} />
      <Toast {...toastProps} />
    </>
  )
}

export default App
